/* oxlint-disable no-console */
// oxlint-disable-next-line no-restricted-imports
import React from 'react';
import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';
import { initAnalyticsSession } from '@gonfalon/analytics/initAnalyticsSession';
import { initCanaryToken } from '@gonfalon/analytics/initCanaryToken';
import { initExperienceTracker } from '@gonfalon/analytics/initExperienceTracker';
import { initSegmentAndCandu } from '@gonfalon/analytics/initSegmentAndCandu';
import { currentEnvironment, currentProject, profile } from '@gonfalon/bootstrap-data/bootstrapData';
import { accountId } from '@gonfalon/constants/constants';
import {
  clearMissingRecentProjectKey as clearMissingRecentProjectKeyFlag,
  fixPendingChanges429,
  frontendQueryCacheGcTime,
  frontendVersionPollingIntervalMs,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import { onDOMReady } from '@gonfalon/dom/onDOMReady';
import { isRESTAPIError } from '@gonfalon/errors/RESTAPIError';
import { hasFrontendVersionOverride } from '@gonfalon/frontend-version/frontendVersionOverrides';
import { initializeFrontendVersionPersistence } from '@gonfalon/frontend-version/initialize';
import { clearVersionMismatchFlag, startVersionPolling } from '@gonfalon/frontend-version/polling';
import { toHref, toLogin } from '@gonfalon/navigator/navigators';
import { initializeQueryClient } from '@gonfalon/react-query-client/getQueryClient';
import { initializeAPIClient } from '@gonfalon/rest-api/client';
import { initializeEnvironment } from '@gonfalon/rest-api/queries/initializeEnvironment';
import { initializeProfile } from '@gonfalon/rest-api/queries/initializeProfile';
import { initializeProject } from '@gonfalon/rest-api/queries/initializeProject';
import { trackPageLoadCustomMetrics } from '@gonfalon/telemetry/trackPageLoadCustomMetrics';
import { QueryCache } from '@tanstack/react-query';
import thunk from 'redux-thunk';

import { epicRegistry } from '#epics/registry';
import { beastModeMiddleware as beastMode } from '#middleware/beastMode';
import { timestampMiddleware as timestamp } from '#middleware/timestamp';
import { createStore } from '#reducers/createStore';
import { init as initializeAccountStorage } from '#sources/AccountLocalStorage';
import { trackOnPageSearchEvent } from '#utils/analyticsUtils';

import '#reducers/router';

import { AppProviders } from '#entryPoints/appProviders';
import { clearMissingRecentProjectKey } from '#query-cache-events/clear-missing-recent-project-key';
import { reportPollError } from '#query-cache-events/report-poll-error';

const isClearMissingRecentProjectKeyEnabled = clearMissingRecentProjectKeyFlag();

/**
 * Extend Window interface to include __resetStore.
 * This supports the ability for the app shell to reset the
 * redux store to mimic the effect of a page refresh.
 */
declare global {
  interface Window {
    __resetStore: ReturnType<typeof createStore>['reset'];
  }
}

export const load = () => {
  const MAX_RETRIES = 3;
  initializeQueryClient({
    queryCache: new QueryCache({
      // Specific query cache event handlers live under the `query-cache-events` directory.
      onError: (error, query) => {
        if (isClearMissingRecentProjectKeyEnabled) {
          clearMissingRecentProjectKey(error);
        }
        reportPollError(error, query);
      },
    }),
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        gcTime: frontendQueryCacheGcTime(),
        staleTime: 10000,
        retry(failureCount, error) {
          if (isRESTAPIError(error)) {
            if (error.status === 404 || error.status === 403) {
              return false;
            }
            // Read per attempt rather than at module load, so the rollout applies without a reload.
            if (error.status === 429 && fixPendingChanges429()) {
              return false;
            }
          }
          return failureCount < MAX_RETRIES;
        },
      },
    },
  });

  const _project = currentProject();
  const _environment = currentEnvironment();
  const _profile = profile();

  if (_project) {
    initializeProject({ projectKey: _project.key, project: _project });

    if (_environment) {
      initializeEnvironment({
        projectKey: _project.key,
        environmentKey: _environment.key,
        environment: _environment,
      });
    }
  }

  if (_profile) {
    initializeProfile({ member: _profile });
  }

  const { store, reset } = createStore({
    middlewares: [thunk, epicRegistry.getMiddleware(), timestamp, beastMode],
  });

  // Sign-out command is registered in routes/app/root.tsx alongside
  // the other top-level commands. The previous loadApp.tsx call into
  // the deleted service-command DOM bus is gone with the rewrite.

  window.__resetStore = reset;

  epicRegistry.run();

  trackOnPageSearchEvent();
  initAnalyticsSession();
  trackPageLoadCustomMetrics();
  initCanaryToken();
  initSegmentAndCandu();
  initExperienceTracker();

  initializeFrontendVersionPersistence();

  // Clear mismatch on boot to avoid a persistent reload signal across sessions.
  // If stale assets remain after reload, polling re-detects mismatch later.
  // This favors eventual update over possible reload loops.
  // Skip polling in dev mode (local manifest won't match staging's /api/status)
  // and when a frontend version override is active (intentional version pinning).
  clearVersionMismatchFlag();
  if (!__DEV__ && !hasFrontendVersionOverride()) {
    startVersionPolling({ intervalMs: frontendVersionPollingIntervalMs() });
  }

  initializeAPIClient({
    onError: (error) => {
      if (!isRESTAPIError(error)) {
        return;
      }

      switch (error.status) {
        case 401:
          const currentLocation = window.location.href;
          const searchParams = new URLSearchParams({ redirect: currentLocation });
          // Unauthenticated pages have a different entry point, hence the full page reload.
          window.location.assign(toHref(toLogin({ search: searchParams })));
          break;
        default:
          break;
      }
    },
  });

  onDOMReady(() => {
    const selector = '.ld-container';
    const container: HTMLElement | null = document.querySelector(selector);

    if (!container) {
      console.warn(`App container selector ${selector} not found, unable to render app`);
      return;
    }

    initializeAccountStorage(accountId() || '');

    if (process.env.NODE_ENV === 'development') {
      // The following rules are turned off because they cause excessive lag.
      const config = {
        rules: [
          { id: 'color-contrast', enabled: false },
          { id: 'duplicate-id-active', enabled: false },
          { id: 'duplicate-id-aria', enabled: false },
          { id: 'duplicate-id', enabled: false },
          { id: 'region', enabled: false },
        ],
        checks: [],
      };
      // Portal nodes such as tooltips & menus are excluded because they cause a massive lag
      const context = { exclude: [['[data-test-id="portal"]'], ['[class*="_Popover-content"]']] };
      import('@axe-core/react')
        .then(({ default: axe }) => {
          axe(React, ReactDOM, 10000, config, context).catch((err) =>
            console.warn('Error testing accessibility w/ axe', err),
          );
        })
        .catch((err) => console.warn('Error importing axe', err));
    }

    const root = createRoot(container);
    root.render(<AppProviders reduxStore={store} />);
  });
};
